import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';
import { IconNames } from 'src/app/_components/icons/icon-names';
import { DeviceUpdateResponse } from 'src/app/_models/api-responses';
import { Device } from 'src/app/_models/device';
import { DeviceSetupStatus } from 'src/app/_models/device-setup-status';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import { Module } from 'src/app/_models/module-enum';
import { LAB_ADMIN_ROLE_LIST, OGI_SUPER_ADMIN_ONLY, Role } from 'src/app/_models/role';
import { TabOptions } from 'src/app/_models/tab-options';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { DeviceService } from 'src/app/_services/device.service';
import { ModalService } from 'src/app/_services/modal.service';
import { deviceNavOptions } from 'src/app/_helpers/device-nav-options';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { InstalledModulesCardComponent } from '../../_components/cards/installed-modules-card/installed-modules-card.component';
import { DeviceCardComponent } from '../../_components/cards/device-card/device-card.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { SoftwareStatusIconComponent } from 'src/app/_components/icons/software-status-icon/software-status-icon.component';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { NumberFormFieldComponent } from 'src/app/_components/forms/number-form-field/number-form-field.component';
import { ExperimentParameterRanges } from 'src/app/_helpers/experiment-min-max';
import { CalibrationService } from 'src/app/_services/calibration.service';
import { UnderlinedOptionTabToggleComponent } from 'src/app/_components/selection-controls/underlined-option-tab-toggle/underlined-option-tab-toggle.component';
import { User } from 'src/app/_models/user';

@Component({
  selector: 'app-device-details',
  templateUrl: './device-details.component.html',
  styleUrls: ['./device-details.component.scss'],
  imports: [
    SoftwareStatusIconComponent,
    IconButtonComponent,
    DeviceCardComponent,
    InstalledModulesCardComponent,
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    NumberFormFieldComponent,
    ButtonComponent,
    ModalComponent,
    ValidationModalComponent,
    RoleCheckPipe,
    ErrorPageComponent,
    LoadingSpinnerComponent,
    UnderlinedOptionTabToggleComponent,
  ],
})
export class DeviceDetailsComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly deviceService = inject(DeviceService);
  private readonly route = inject(ActivatedRoute);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly calibrationService = inject(CalibrationService);
  private readonly sanitizer = inject(DomSanitizer);

  // Control vars
  error: ErrorObject;
  getDeviceError = signal<ErrorObject | null>(null);
  isProcessing = false;
  isLoading = signal(true);
  deviceId: string | null = null;
  device = signal<Device | null>(null);
  updateDeviceSuccessMessage: string;
  hasPhModule = computed(() =>
    this.device()?.deviceModules?.includes(Module.pH) ?? false,
  );
  hasLCModule = computed(() =>
    this.device()?.deviceModules?.includes(Module.LiquidControl) ?? false,
  );

  minPumpTubeLength = ExperimentParameterRanges.PUMP_TUBE_LENGTH_CM.min;
  maxPumpTubeLength = ExperimentParameterRanges.PUMP_TUBE_LENGTH_CM.max;
  pumpTubeLengthStep = ExperimentParameterRanges.PUMP_TUBE_LENGTH_CM.step;

  // Navigation
  navOptions: TabOptions[];
  allowNavigation: Subject<boolean> = new Subject<boolean>();
  private readonly unsubscribe$ = new Subject<void>();
  private readonly cancelDeviceFetch$ = new Subject<void>();

  laboratory: Laboratory | null;
  currentUser: User | null;

  // Enums for use in the template
  Role = Role;
  readonly ogiSuperAdminOnly = OGI_SUPER_ADMIN_ONLY;
  readonly labAdminRoles = LAB_ADMIN_ROLE_LIST;
  Module = Module;
  DeviceSetupStatus = DeviceSetupStatus;

  /**
   * @param {IconNames} loadingSpinner The loading placeholder
   */
  spinner = IconNames.Sync;

  nameField = new FormBase({
    key: 'name',
    label: 'Name of device',
    type: 'text',
    placeholder: 'Name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  pumpTubeLengthField = new FormBase<number>({
    key: 'pumpTubeLength',
    label: 'Tube length (cm)',
    type: 'range',
    placeholder: 'Not set',
    disabled: false,
    required: false,
    value: null,
    options: [],
  });

  deviceForm = this.formBuilder.group({
    // Explicit typing for string field
    [this.nameField.key]: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(100),
      Validators.required,
    ]),
    [this.pumpTubeLengthField.key]: new FormControl(
      this.pumpTubeLengthField.value,
      [
        Validators.min(this.minPumpTubeLength),
        Validators.max(this.maxPumpTubeLength),
      ],
    ),
  });

  ngOnInit(): void {
    // Use combineLatest so nav options are recalculated whenever either the
    // authenticated user or the route params change
    combineLatest([
      this.authenticationService.currentUser$,
      this.route.paramMap,
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([user, params]) => {
        if (!user) return; // logout emission — do not proceed
        this.currentUser = user;
        this.laboratory = this.authenticationService.selectedLaboratory;
        const deviceId = params.get('id');
        this.navOptions = deviceNavOptions(deviceId, this.currentUser.role);
        // Only fetch device data when the device ID changes, not on every user re-emission
        if (deviceId !== this.deviceId) {
          this.deviceId = deviceId;
          this.getDeviceFromApi();
        }
      });
  }

  icon(iconName: string): SafeHtml {
    let pathString = '';
    pathString = iconName.toString();
    return this.sanitizer.bypassSecurityTrustHtml(pathString);
  }

  getDeviceFromApi(): void {
    // Cancel any still-in-flight request from a previous device before starting a new one
    this.cancelDeviceFetch$.next();
    this.getDeviceError.set(null);
    this.isLoading.set(true);
    this.device.set(null);
    this.deviceService.getDeviceById(this.deviceId).pipe(takeUntil(this.cancelDeviceFetch$), takeUntil(this.unsubscribe$)).subscribe({
      next: (device) => {
        this.device.set(device);
        this.initialiseForm();
        this.isLoading.set(false);
      },
      error: (error: ErrorObject) => {
        this.getDeviceError.set(error);
        this.isLoading.set(false);
      },
    });
  }

  initialiseForm(): void {
    if (!this.currentUser) return;

    this.deviceForm.patchValue({
      [this.nameField.key]: this.device()!.name,
      [this.pumpTubeLengthField.key]: this.device()!.pumpTubeLength || null,
    });
    this.deviceForm.markAsPristine();

    // Disable form for read-only roles; submission is separately blocked by isUpdateButtonDisabled()
    if (LAB_ADMIN_ROLE_LIST.includes(this.currentUser.role)) {
      this.deviceForm.enable();
    } else {
      this.deviceForm.disable();
    }
  }

  isUpdateButtonDisabled(): boolean {
    return (
      this.isLoading() ||
      this.deviceForm.invalid ||
      !this.deviceForm.dirty ||
      this.isProcessing ||
      this.device()?.deviceStatusId !== DeviceSetupStatus.Inactive
    );
  }

  updateDevice(): void {
    // If form is already being submitted, exit
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.error = null;
    // Update device object
    const deviceUpdate = {
      id: this.device()!.id,
      name: this.deviceForm.value.name as string,
      pumpTubeLength: this.deviceForm.value.pumpTubeLength as number,
    };
    // Send to API
    this.deviceService.updateDevice(deviceUpdate).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data: DeviceUpdateResponse) => {
        const { device, message } = data;
        if (device) {
          this.device.set(device);
          this.initialiseForm();
        }
        this.isProcessing = false;
        this.updateDeviceSuccessMessage = message;
        this.openModal('update-device');
      },
      error: (error: ErrorObject) => {
        this.error = error;
        this.isProcessing = false;
        this.openModal('error');
      },
    });
  }

  deleteDevice(): void {
    this.error = null;
    this.deviceService.deleteDevice(this.deviceId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data: DeviceUpdateResponse) => {
        this.updateDeviceSuccessMessage = data.message;
        this.modalService.close('delete-device');
        this.modalService.open('update-device');
      },
      error: (error: ErrorObject) => {
        this.error = error;
        this.modalService.close('delete-device');
        this.openModal('error');
      },
    });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'close-error-button':
        this.modalService.close('error');
        break;
      case 'close-success-button':
        this.modalService.close('update-device');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.modalService.close('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.modalService.close('attention');
        break;
      case 'confirm-delete-button':
        this.deleteDevice();
        break;
      case 'cancel-delete-button':
        this.modalService.close('delete-device');
        break;
      default:
        break;
    }
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.cancelDeviceFetch$.complete();
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.deviceForm.pristine || !this.currentUser) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation.pipe(take(1)).subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
